import React, { useEffect, useLayoutEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./AuthLanding.css";
import loginHero from "../../assets/login-hero.png";
import { startMicrosoftPkceRedirect } from "../../utils/microsoftSso";
import api, { clearStoredAuthTokens, resolveApiErrorMessage } from "../../services/axios";
import {
  checkAuthSession,
  clearFreshLogin,
  clearSessionEndedMarker,
  isSessionEndedLocally,
  postLoginTarget,
  trapLoginPageHistory,
  markFreshLogin,
  markSsoAuthPending,
  pinAuthEmail,
  isSsoAuthPending,
} from "../../utils/authSession";

const REACHOUT_BRIDGE_KEY = "reachout_wizetok_bridge_attempted";

/**
 * Silently checks whether the user already has a Wizetok session, without the
 * full-page bounce a direct window.location.replace(bridgeUrl) would cause —
 * loads the bridge page in a hidden iframe instead; Wizetok's
 * CrossAppReachOutBridge detects it's framed and postMessages the result back
 * rather than navigating. On a miss, nothing visible ever happens: the login
 * form the user is already looking at just stays put. On a hit, we do exactly
 * one real navigation to complete the handoff login.
 */
function checkWizetokSessionSilently(bridgeUrl, expectedOrigin) {
  let settled = false;
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.setAttribute("aria-hidden", "true");
  iframe.src = bridgeUrl;

  const cleanup = () => {
    window.removeEventListener("message", onMessage);
    window.clearTimeout(timeoutId);
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };

  const onMessage = (event) => {
    if (settled) return;
    if (event.origin !== expectedOrigin) return;
    if (event.data?.type !== "reachout-bridge-result") return;
    settled = true;
    cleanup();
    if (event.data.ok && event.data.redirectUrl) {
      window.location.replace(event.data.redirectUrl);
    }
    // Miss: stay exactly where we are — the login form is already showing.
  };

  const timeoutId = window.setTimeout(() => {
    if (settled) return;
    settled = true;
    cleanup();
  }, 4000);

  window.addEventListener("message", onMessage);
  document.body.appendChild(iframe);
}

const MicrosoftMark = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 21 21" fill="none" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

const Index = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sessionNotice, setSessionNotice] = useState("");
  const [method, setMethod] = useState("microsoft");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    try {
      const reason = sessionStorage.getItem("auth_expired_reason");
      if (reason) {
        setSessionNotice(reason);
        sessionStorage.removeItem("auth_expired_reason");
      }
    } catch (e) {
      // ignore storage access issues
    }

    const params = new URLSearchParams(location.search);
    if (params.get("server_starting") === "1") {
      setSessionNotice("The server is still starting up. Please wait 20–30 seconds and try signing in again.");
    }
    if (params.get("sso_retry") === "1") {
      setError("Microsoft sign-in did not complete. Please try again.");
      setMethod("microsoft");
    }
    if (params.get("not_invited") === "1") {
      try {
        sessionStorage.removeItem("sso_login_error");
      } catch (e) {
        // ignore
      }
      navigate("/unauthorized", { replace: true });
      return;
    }

    const removeHistoryTrap = trapLoginPageHistory();

    return () => {
      if (typeof removeHistoryTrap === "function") removeHistoryTrap();
    };
  }, [location.search, navigate]);

  useLayoutEffect(() => {
    const params = new URLSearchParams(location.search);
    const loginHint = (params.get("login_hint") || "").trim();
    const handoff = (params.get("handoff") || "").trim();
    if (params.get("not_invited") === "1") {
      return undefined;
    }

    // Cross-app SSO from Wizetok: allow session even after a local logout marker.
    if (loginHint || handoff) {
      clearSessionEndedMarker();
    }

    // After an explicit logout, stay on this page. Do not probe /auth/me —
    // leftover cookies used to bounce the user straight back into the app.
    const stayOnLoginIfUnauthed = isSessionEndedLocally() && !loginHint && !handoff;

    let alive = true;
    void (async () => {
      // Token exchange from Wizetok — set ReachOut cookies, skip Microsoft.
      if (handoff) {
        try {
          setSubmitting(true);
          await api.post("/auth/cross-app/consume", { code: handoff, source: "wizetok" });
          if (!alive) return;
          sessionStorage.removeItem(REACHOUT_BRIDGE_KEY);
          window.history.replaceState({}, "", "/login");
          window.location.replace(await postLoginTarget());
          return;
        } catch {
          if (!alive) return;
          // Silent SSO failed. Drop ?handoff= so refresh cannot replay an expired
          // token, and stay on the normal login form (no "expired" banner).
          setSubmitting(false);
          navigate("/login", { replace: true });
          return;
        }
      }

      if (stayOnLoginIfUnauthed && !isSsoAuthPending()) {
        return;
      }

      // /login is the switch-account page. Do not auto-enter as whoever
      // still has a leftover cookie — that kept showing the previous user.
      const switchingAccount = (location.pathname === "/login" || location.pathname === "/credentials-login")
        && !handoff
        && !loginHint
        && !isSsoAuthPending();
      if (switchingAccount) {
        return;
      }

      const authed = await checkAuthSession({ ignoreEndedMarker: true });
      if (!alive) return;
      if (authed) {
        sessionStorage.removeItem(REACHOUT_BRIDGE_KEY);
        const returnTo = (params.get("return_to") || sessionStorage.getItem("reachout_return_to") || "").trim();
        sessionStorage.removeItem("reachout_return_to");
        if (returnTo.startsWith("/sso/")) {
          navigate(returnTo, { replace: true });
          return;
        }
        window.location.replace(await postLoginTarget());
        return;
      }

      if (stayOnLoginIfUnauthed) return;

      // Handoff already handled (or failed) above.
      if (handoff) return;

      // Cross-app launch with login_hint — start Microsoft once.
      if (loginHint) {
        const already = sessionStorage.getItem("reachout_cross_app_sso");
        if (already === loginHint) return;
        sessionStorage.setItem("reachout_cross_app_sso", loginHint);
        try {
          markSsoAuthPending();
          setSubmitting(true);
          await startMicrosoftPkceRedirect({ loginHint });
        } catch (err) {
          sessionStorage.removeItem("reachout_cross_app_sso");
          setError(err?.message || "Microsoft sign-in failed");
          setSubmitting(false);
        }
        return;
      }

      const returnTo = (params.get("return_to") || "").trim();
      if (returnTo.startsWith("/sso/")) {
        sessionStorage.setItem("reachout_return_to", returnTo);
        return;
      }

      // Do not auto-SSO from Wizetok here. That silently replaced a ReachOut
      // password login (Sony) with whoever was signed into Wizetok (Vijay) on refresh.
      // Cross-app login only runs when this page is opened with ?handoff= or ?login_hint=.
    })();

    return () => {
      alive = false;
    };
  }, [navigate, location.search, location.pathname]);

  const selectMethod = (next) => {
    setMethod(next);
    setError("");
    setSessionNotice("");
    if (next !== "otp") {
      setOtpSent(false);
      setOtp("");
    }
  };

  const finishLocalLogin = async (serverEmail) => {
    clearSessionEndedMarker();
    clearFreshLogin();
    pinAuthEmail(serverEmail);
    window.location.replace(await postLoginTarget());
  };

  const wipePreviousSession = async () => {
    markFreshLogin();
    try {
      await api.post("/auth/logout");
    } catch {
      // not signed in, or leftover cookies already invalid
    }
    clearStoredAuthTokens();
  };

  const handleTenantBlocked = (data) => {
    try {
      sessionStorage.setItem("blocked_user", JSON.stringify({
        email: data?.userEmail || email || "",
        firstName: data?.userFirstName || "",
        lastName: data?.userLastName || "",
      }));
    } catch {
      // ignore
    }
    navigate("/unauthorized", { replace: true });
  };

  const isOtpUnauthorized = (err) => {
    const data = err.response?.data || {};
    return err.response?.status === 403 && (
      data.code === "OTP_NOT_AUTHORIZED" ||
      /not authorized for OTP/i.test(String(data.message || data.detail || ""))
    );
  };

  const isAccessBlocked = (err) => {
    if (err.response?.status !== 403) return false;
    const data = err.response?.data || {};
    const message = String(data.message || data.detail || "");
    return (
      data.code === "NOT_TENANT_MEMBER" ||
      data.code === "NOT_INVITED" ||
      /not been invited/i.test(message)
    );
  };

  const handleMicrosoftLogin = async () => {
    try {
      setSubmitting(true);
      await wipePreviousSession();
      markSsoAuthPending();
      await startMicrosoftPkceRedirect({
        prompt: "login",
      });
    } catch (e) {
      setSubmitting(false);
      setError(e.message || "Could not start Microsoft sign-in.");
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSessionNotice("");
    setSubmitting(true);
    try {
      await wipePreviousSession();
      const { data } = await api.post("/auth/credentials-login", {
        identifier: email.trim(),
        password,
      });
      finishLocalLogin(data?.user?.email);
    } catch (err) {
      const data = err.response?.data;
      if (isAccessBlocked(err)) {
        handleTenantBlocked(data);
        return;
      }
      setError(resolveApiErrorMessage(err, "Invalid email or password"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/otp/request", { email: email.trim() });
      setOtp("");
      setOtpSent(true);
    } catch (err) {
      if (isOtpUnauthorized(err)) {
        setError(resolveApiErrorMessage(err, "This email is not authorized for OTP login."));
        return;
      }
      if (isAccessBlocked(err)) {
        handleTenantBlocked(err.response?.data);
        return;
      }
      setError(resolveApiErrorMessage(err, "Failed to send OTP"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await wipePreviousSession();
      const { data } = await api.post("/auth/otp/verify", { email: email.trim(), code: otp.trim() });
      finishLocalLogin(data?.user?.email);
    } catch (err) {
      const data = err.response?.data;
      if (isOtpUnauthorized(err)) {
        setError(resolveApiErrorMessage(err, "This email is not authorized for OTP login."));
        return;
      }
      if (isAccessBlocked(err)) {
        handleTenantBlocked(data);
        return;
      }
      setError(resolveApiErrorMessage(err, "Invalid or expired OTP"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-left-pane">
        <div className="auth-card">
          <div className="auth-login-stack">
            <div className="auth-logo">
              <img
                src="/feuji-logo.png"
                alt="feuji"
                className="auth-logo-img"
              />
              <div className="auth-product">
                <span>ReachOu</span>
                <span className="accent">T</span>
              </div>
            </div>

            <div className="auth-method-tabs" role="tablist" aria-label="Sign-in method">
              <button
                type="button"
                role="tab"
                aria-selected={method === "microsoft"}
                className={`auth-method-tab${method === "microsoft" ? " is-active" : ""}`}
                onClick={() => selectMethod("microsoft")}
              >
                <MicrosoftMark size={14} />
                Microsoft
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={method === "password"}
                className={`auth-method-tab${method === "password" ? " is-active" : ""}`}
                onClick={() => selectMethod("password")}
              >
                Password
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={method === "otp"}
                className={`auth-method-tab${method === "otp" ? " is-active" : ""}`}
                onClick={() => selectMethod("otp")}
              >
                OTP
              </button>
            </div>

            <div className="auth-actions">
              {sessionNotice ? (
                <div
                  style={{
                    marginBottom: "4px",
                    color: sessionNotice.includes("starting") ? "#0c5460" : "#842029",
                    background: sessionNotice.includes("starting") ? "#d1ecf1" : "#f8d7da",
                    border: `1px solid ${sessionNotice.includes("starting") ? "#bee5eb" : "#f5c2c7"}`,
                    borderRadius: "6px",
                    padding: "8px 10px",
                    fontSize: "13px",
                    textAlign: "left",
                  }}
                >
                  {sessionNotice}
                </div>
              ) : null}

              {error ? <div className="auth-error">{error}</div> : null}

              {method === "microsoft" && (
                <>
                  <p className="auth-pane-copy">Sign in with your Microsoft / company account.</p>
                  <button
                    type="button"
                    onClick={handleMicrosoftLogin}
                    className="auth-btn auth-btn-primary"
                    disabled={submitting}
                  >
                    <MicrosoftMark size={16} />
                    Sign in with Microsoft
                  </button>
                </>
              )}

              {method === "password" && (
                <form className="auth-form" onSubmit={handlePasswordLogin}>
                  <label className="auth-label" htmlFor="reachout-email">Email Address</label>
                  <input
                    id="reachout-email"
                    type="email"
                    className="auth-input"
                    autoComplete="username"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <label className="auth-label" htmlFor="reachout-password">Password</label>
                  <input
                    id="reachout-password"
                    type="password"
                    className="auth-input"
                    autoComplete="current-password"
                    placeholder="Enter your Microsoft / company password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button type="submit" className="auth-btn auth-btn-primary" disabled={submitting}>
                    {submitting ? "Signing in…" : "Sign in"}
                  </button>
                </form>
              )}

              {method === "otp" && !otpSent && (
                <form className="auth-form" onSubmit={handleSendOtp}>
                  <label className="auth-label" htmlFor="reachout-otp-email">Email Address</label>
                  <input
                    id="reachout-otp-email"
                    type="email"
                    className="auth-input"
                    autoComplete="username"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <button type="submit" className="auth-btn auth-btn-primary" disabled={submitting}>
                    {submitting ? "Sending OTP…" : "Send OTP"}
                  </button>
                </form>
              )}

              {method === "otp" && otpSent && (
                <form className="auth-form" onSubmit={handleVerifyOtp}>
                  <p className="auth-pane-copy">Enter the OTP sent to {email}</p>
                  <label className="auth-label" htmlFor="reachout-otp-code">OTP Code</label>
                  <input
                    id="reachout-otp-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    className="auth-input auth-input-otp"
                    placeholder="Enter 6-digit OTP"
                    maxLength={8}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\s/g, ""))}
                    required
                  />
                  <button
                    type="submit"
                    className="auth-btn auth-btn-primary"
                    disabled={submitting || otp.trim().length < 4}
                  >
                    {submitting ? "Verifying…" : "Verify OTP"}
                  </button>
                  <button
                    type="button"
                    className="auth-text-btn"
                    onClick={handleSendOtp}
                    disabled={submitting}
                  >
                    Resend OTP
                  </button>
                  <button
                    type="button"
                    className="auth-text-btn"
                    onClick={() => { setOtpSent(false); setOtp(""); setError(""); }}
                  >
                    Use a different email
                  </button>
                </form>
              )}
            </div>

            <div className="auth-secure">Secure login powered by your organization.</div>

            <div className="auth-footer">
              <div className="auth-copyright">
                © 2026 Feuji Inc. All rights reserved.
              </div>
              <div className="auth-links">
                <a href="/login">Cookie Policy</a>
                <a href="/login">Terms Of Use</a>
                <a href="/login">Privacy Policy</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="auth-right-pane">
        <img
          src={loginHero}
          alt="ReachOut Recruitment"
          className="auth-illustration"
        />
      </div>
    </div>
  );
};

export default Index;
